<template>
  <div class="mx-auto mb-24 max-w-7xl">
    <div class="m-4">
      <div v-if="count > 0" class="my-4" data-testid="wishlist-headline">
        <Headline tag="h1" look="h2" weight="normal">
          {{ $t('wishlist.heading') }} ({{ count }})
        </Headline>
      </div>
      <template v-if="fetching">
        <Skeleton data-testid="wishlist-skeleton"></Skeleton>
      </template>
      <template v-else>
        <div v-if="count > 0" data-testid="wishlist-content">
          <div v-if="!isLoggedIn" data-testid="save-in-account-section">
            <p class="inline">
              {{ $t('wishlist.save_in_account') }}
              <FimLink
                :to="localePath(routeList.signin)"
                hover="underline"
                text-size="md"
                type="quiet"
                data-testid="login-link"
                >{{ $t('wishlist.log_in_here') }}
              </FimLink>
            </p>
          </div>
          <TransitionGroup
            v-if="isPrismaDesignEnabled"
            name="wishlist-item"
            tag="div"
            class="mt-8 grid w-auto grid-cols-12 gap-4"
            data-testid="wishlist-product-list"
            enter-from-class="opacity-0 scale-95 translate-y-4"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            enter-active-class="transition-all duration-300 ease-out"
            leave-from-class="opacity-100 scale-100 translate-y-0"
            leave-to-class="opacity-0 scale-95 -translate-y-4"
            leave-active-class="transition-all duration-300 ease-in"
            move-class="transition-all duration-300 ease-out"
          >
            <div
              v-for="product in products"
              :key="`product-${product.id}`"
              class="wishlist-product-wrapper col-span-12 sm:col-span-6 xl:col-span-4 2xl:col-span-3"
            >
              <ProductCard
                :product="product"
                :loading="fetching"
                @click:select-item="trackProductClick"
              >
                <template #vto-box>
                  <div
                    v-if="hasVto(product)"
                    class="mt-4 flex h-full"
                    aria-hidden="true"
                  >
                    <PlpVTOButton
                      class="mt-auto"
                      :product="product"
                      :variant="product.variants?.[0]"
                      :disabled="!isProductVTOAvailable(product.id)"
                    />
                  </div>
                </template>
              </ProductCard>
            </div>
          </TransitionGroup>
          <div
            v-else
            class="mt-3 grid w-auto grid-cols-12 border-t border-neutral-20 sm:border-l"
            :class="{
              'lg:w-1/3 lg:grid-cols-4': count === 1,
              'lg:w-2/3 lg:grid-cols-8': count === 2,
            }"
            data-testid="wishlist-cards-grid"
          >
            <WishlistCard
              v-for="(entry, idx) in items"
              :id="entry.product.id"
              :key="`product-${entry.product.id}-${
                entry.variantId || ''
              }-${idx}`"
              :link="getProductDetailRoute(entry.product)"
              :price="getCombinedProductPrice(entry.product)"
              :entry="entry"
              :size="
                getSizeFromVariant(
                  // @ts-ignore
                  getVariant(
                    entry.product.variants || [],
                    entry.variantId || (entry.product?.variants?.[0]?.id ?? 0),
                  ),
                )?.label || ''
              "
              :variants="entry.product.variants"
              :title="getFirstAttributeLabel(entry.product.attributes, 'brand')"
              :name="getFirstAttributeLabel(entry.product.attributes, 'name')"
              :is-available="
                entry?.variant
                  ? isVariantAvailable(entry.product, entry.variant)
                  : isProductAvailable(entry.product)
              "
              :image="
                getImageFromList(
                  entry.product.images,
                  ProductImageType.BUST,
                  'front',
                ) || undefined
              "
              :is-in-basket="
                isInBasket({
                  productId: entry.product.id,
                })
              "
              @click:select-item="trackSelectItem(entry.product)"
            />
          </div>
        </div>
        <div
          v-if="count === 0"
          class="mt-8 pt-28 text-center"
          data-testid="wishlist-empty-state"
        >
          <LazyIconFielmannBrokenHeart
            class="mx-auto mb-5 size-24 text-red-400"
            data-testid="empty-heart-icon"
          />
          <div class="m-auto max-w-lg">
            <h1 class="text-2xl font-medium" data-testid="empty-state-title">
              {{ $t('wishlist.no_items_info') }}
            </h1>
            <I18nT
              keypath="wishlist.select_favorites_info"
              tag="p"
              class="mt-4"
              data-testid="empty-state-description"
            >
              <template #loginInfo>
                <FimLink
                  :to="localePath(routeList.signin)"
                  text-size="md"
                  class="border-b border-gray-500 hover:border-black"
                  data-testid="empty-state-login-link"
                >
                  {{ $t('wishlist.login_intext_information') }}
                </FimLink>
              </template>
            </I18nT>
            <p class="mt-4" data-testid="continue-shopping-text">
              {{ $t('wishlist.continue_shopping_info') }}
            </p>
          </div>

          <div class="mt-6 flex justify-center space-x-4">
            <ReturnToShoppingButton data-testid="return-to-shopping-button" />
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  getSizeFromVariant,
  getVariant,
  getImageFromList,
  type Product,
} from '@scayle/storefront-nuxt'
import { useProductPrice } from '~/composables/useProductPrice'
import { getFirstAttributeLabel } from '~/utils/attribute'
import { hasVto } from '~/utils/product'
import type { FimWishlistItem } from '~/composables/useFimWishlist'

const localePath = useLocalePath()
const wishlist = await useWishlist()
const basket = await useFimBasket()
const { trackSelectItem } = await useTrackingEvents()
const { isLoggedIn } = await useUser({ immediate: false, lazy: true })
const { getProductDetailRoute } = useRouteHelpers()
const { getCombinedProductPrice } = await useProductPrice()

// VTO functionality
const { checkBatchAvailability, isProductVTOAvailable } =
  useVTOBatchAvailability()

const items = computed(() => {
  return (wishlist.items.value || []) as FimWishlistItem[]
})

const products = computed(() => {
  return items.value.map((item) => item.product)
})

// Check VTO availability for all products when they change
watch(
  () => products.value,
  (products) => {
    if (import.meta.client && products?.length) {
      checkBatchAvailability(products)
    }
  },
  { immediate: true },
)

const count = computed(() => {
  return wishlist.count.value || 0
})

const fetching = computed(() => {
  return wishlist.fetching.value
})

const isInBasket = (item: { productId: number }) => {
  return basket.contains(item)
}

const $i18n = useI18n()

useSeoMeta({
  robots: 'noindex,follow',
  title: $i18n.t('wishlist.seo_title'),
})

defineOptions({ name: 'WishlistPage' })
definePageMeta({ pageType: 'wishlist_page' })

const { isPrismaDesignEnabled } = useFeatureToggles()

const trackProductClick = (product: Product) => {
  trackSelectItem(product)
}
</script>

<style scoped>
@media (prefers-reduced-motion: reduce) {
  .wishlist-item-move,
  .wishlist-item-enter-active,
  .wishlist-item-leave-active {
    transition: opacity 0.2s ease;
    transform: none;
  }

  .wishlist-item-enter-from,
  .wishlist-item-leave-to {
    transform: none;
  }
}
</style>
